import { ISectionLogger } from "@multiweb/logging";
import { HTTPError } from "Global/Base/Comunication/HTTPRequest";
import { UUID } from "../../Global/Base/IDs/UUID";
import { CancelError, CancellationToken } from "../../Global/Base/Promises/CancellationTokenSource";
import { Deferred, PromiseUtils } from "../../Global/Base/Promises/PromiseUtils";
import { IDBException, IDBExceptions } from "../../IndexedDB/Promises/IndexedDBExceptions";
import { OWExceptions } from "../Basic/OWExceptions";
import { WorkerMessage, WorkerResponseMessage } from "../Basic/WorkerMessages";

export const workerSymbol = Symbol.for("tscWorker");

export class WorkerCommunicator {
    private _worker: AbstractWorker;
    private _port: MessagePort;
    private _channel: MessageChannel;
    private static _logger: ISectionLogger | undefined;
    public static workersLoaded: Array<Promise<WorkerCommunicator>> = [];

    public static initialize(logger: ISectionLogger): void {
        WorkerCommunicator._logger = logger;
    }

    public static registerWorker(workerUrl: string, getParentCommunicator?: () => Promise<WorkerCommunicator>): Promise<WorkerCommunicator> {
        const prom = PromiseUtils.defer<WorkerCommunicator>();
        const w: WorkerCommunicator = new WorkerCommunicator(); // komunikátor se musí založit vždy nový pro každý kontext
        let worker: Worker;

        //WorkerCommunicator._logger.log("Start register worker: %s", workerUrl);

        const f_registerEventData = () => {
            w._channel = new MessageChannel();
            w._port = w._channel.port1;
            w._port.onmessage = w._onWorkerMessage.bind(w);

            worker.postMessage({ type: "connect" }, [w._channel.port2]);
            w._worker = worker;

            self.addEventListener("unload", () => WorkerCommunicator._onUnload(w));
            //WorkerCommunicator._logger.log("Worker registered: %s", workerUrl);
            prom.resolve(w);
        };

        const f_initWorker = () => {
            //worker = new (Worker as any)(workerUrl, { type: "module" });
            worker = new Worker(workerUrl);
            worker.onerror = (e) => {
                WorkerCommunicator._logger.error(e);
                prom.reject(e);
            };
            worker.onmessage = (e) => {
                if (e.data == "WorkerLoaded") {
                    f_registerEventData();
                }
            };
            //WorkerCommunicator._logger.log("New Worker created: %s", workerUrl);
        };

        if (typeof getParentCommunicator === "function") {
            getParentCommunicator().then((parentCommunicator) => {
                if (parentCommunicator && parentCommunicator._worker) {
                    worker = parentCommunicator._worker as Worker;
                    f_registerEventData();
                } else {
                    f_initWorker();
                }
            });
        } else
            f_initWorker();

        this.workersLoaded.push(prom.promise);

        return prom.promise;
    }

    private static _onUnload(w: WorkerCommunicator): void {
        try {
            const port = w._port;
            w._worker = null;
            w._port = null;
            port.postMessage({ type: "disconnect" });
            port.close();
        } catch (ex) {
            WorkerCommunicator._logger.debug(ex);
        }
    }

    private _waitingPromises: Map<string, Deferred<any>> = new Map();

    private _onWorkerMessage(e: MessageEvent): void {
        const response = e.data as WorkerResponseMessage;
        for (const i of response.items) {
            const waiter = this._waitingPromises.get(i.id);
            if (waiter) {
                if (i.exception) {
                    let error: OWExceptions | IDBException | Error;
                    if (!Reflect.has(i.exception, "typeName")) {
                        error = new Error(i.exception.response);
                    } else {
                        const owExcType = OWExceptions.getRegisteredExceptionType(i.exception.typeName);
                        const idbExcType = IDBExceptions.getRegisteredExceptionType(i.exception.typeName);
                        if (owExcType)
                            error = new owExcType(i.exception.response);
                        else if (idbExcType)
                            error = new idbExcType(i.exception.response);
                        else {
                            if (i.exception.typeName) {
                                switch (i.exception.typeName) {
                                    case HTTPError.constructor.name:
                                        error = OWExceptions.parse(i.exception);
                                        break;
                                    case CancelError.constructor.name:
                                        error = new CancelError();
                                        break;
                                    default:
                                        error = new Error(i.exception.response);
                                }
                            } else
                                error = new Error(i.exception.toString());
                        }
                    }
                    waiter.reject(error);
                } else
                    waiter.resolve(i.data);
                this._waitingPromises.delete(i.id);
            }
        }
        if (this.onmessage)
            this.onmessage(response);
    }

    public onmessage: (msg: WorkerResponseMessage) => void;

    public sendMessage(msg: WorkerMessage, cancellationToken: CancellationToken = CancellationToken.none): Promise<any> {
        const def = PromiseUtils.defer<any>();
        if (!msg.id)
            msg.id = UUID.generate();
        cancellationToken.register(() => this._port.postMessage({ type: "cancel", id: msg.id }));
        this._waitingPromises.set(msg.id, def);
        this._port.postMessage(msg);

        return def.promise;
    }

    public sendOneWayMessage(msg: WorkerMessage): void {
        if (!msg.id)
            msg.id = UUID.generate();
        this._port.postMessage(msg);
    }
}
